import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";
import type { MerchantOfferAddressDetailVO } from "@/api/merchant.api";

export type MerchantOfferAddressFormRow = {
  /** From `getOffersOfMerchantFromDB` → `addressDetailsVOList[].id` */
  id?: number;
  address: string;
  city: string;
  country: string;
  location: string;
  phoneCountryCode: string;
  phoneNumber: string;
};

export function createEmptyMerchantOfferAddressRow(): MerchantOfferAddressFormRow {
  return {
    address: "",
    city: "",
    country: "",
    location: "",
    phoneCountryCode: "+971",
    phoneNumber: "",
  };
}

export function voToAddressFormRow(
  vo: MerchantOfferAddressDetailVO
): MerchantOfferAddressFormRow {
  return {
    ...(vo.id != null && Number.isFinite(vo.id) ? { id: vo.id } : {}),
    address: vo.address ?? "",
    city: vo.city ?? "",
    country: vo.country ?? "",
    location: vo.location ?? "",
    phoneCountryCode: vo.phoneCountryCode ?? "",
    phoneNumber: vo.phoneNumber ?? "",
  };
}

/**
 * Builds API payload from form rows. Returns `null` if no addresses were entered.
 * If any row is partially filled, returns `{ error: string }`.
 */
export function buildMerchantOfferAddressPayload(
  rows: MerchantOfferAddressFormRow[]
):
  | { addressDetailsVOList: MerchantOfferAddressDetailVO[] }
  | { error: string }
  | null {
  const normalized = rows.map((r) => ({
    id: r.id,
    address: r.address.trim(),
    city: r.city.trim(),
    country: r.country.trim(),
    location: r.location.trim(),
    phoneCountryCode: r.phoneCountryCode.trim(),
    phoneNumber: r.phoneNumber.trim().replace(/\D/g, ""),
  }));

  const nonEmptyRows = normalized.filter(
    (r) =>
      r.address ||
      r.city ||
      r.country ||
      r.location ||
      r.phoneCountryCode ||
      r.phoneNumber
  );

  if (nonEmptyRows.length === 0) return null;

  for (const r of nonEmptyRows) {
    if (
      !r.address ||
      !r.city ||
      !r.country ||
      !r.location ||
      !r.phoneCountryCode ||
      !r.phoneNumber
    ) {
      return {
        error:
          "Each address with any field filled must include address, city, country, location, phone country code, and phone number.",
      };
    }
  }

  return {
    addressDetailsVOList: nonEmptyRows.map((r) => {
      const vo: MerchantOfferAddressDetailVO = {
        address: r.address,
        city: r.city,
        country: r.country,
        location: r.location,
        phoneCountryCode: r.phoneCountryCode,
        phoneNumber: r.phoneNumber,
      };
      if (r.id != null && Number.isFinite(r.id)) {
        vo.id = r.id;
      }
      return vo;
    }),
  };
}

/** Reads offer row id from create/update offer response (axios `data`). */
export function extractOffersOfMerchantIdFromResponse(response: {
  data?: unknown;
}): number | null {
  const body = response?.data;
  if (body == null || typeof body !== "object") return null;
  const o = body as Record<string, unknown>;
  for (const key of ["id", "offersOfMerchantId", "offerId"] as const) {
    const v = o[key];
    if (v != null && v !== "") {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
  }
  return null;
}

type MerchantOfferAddressDetailsSectionProps = {
  rows: MerchantOfferAddressFormRow[];
  onChange: (rows: MerchantOfferAddressFormRow[]) => void;
};

export function MerchantOfferAddressDetailsSection({
  rows,
  onChange,
}: MerchantOfferAddressDetailsSectionProps) {
  const updateRow = (
    index: number,
    patch: Partial<MerchantOfferAddressFormRow>
  ) => {
    onChange(
      rows.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const removeRow = (index: number) => {
    if (rows.length <= 1) {
      onChange([createEmptyMerchantOfferAddressRow()]);
      return;
    }
    onChange(rows.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Addresses</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Optional. Add one or more locations for this offer. All fields are
          required for each address you add.
        </p>
      </div>

      <div className="space-y-4">
        {rows.map((row, index) => (
          <div
            key={index}
            className="rounded-md border border-gray-200 bg-white p-4 space-y-3 shadow-sm"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-gray-500">
                Address {index + 1}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => removeRow(index)}
                aria-label={`Remove address ${index + 1}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`offer-addr-address-${index}`}>Address</Label>
              <Textarea
                id={`offer-addr-address-${index}`}
                placeholder="Street / building"
                value={row.address}
                onChange={(e) => updateRow(index, { address: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor={`offer-addr-city-${index}`}>City</Label>
                <Input
                  id={`offer-addr-city-${index}`}
                  placeholder="City"
                  value={row.city}
                  onChange={(e) => updateRow(index, { city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`offer-addr-country-${index}`}>Country</Label>
                <Input
                  id={`offer-addr-country-${index}`}
                  placeholder="Country"
                  value={row.country}
                  onChange={(e) =>
                    updateRow(index, { country: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={`offer-addr-location-${index}`}>Location</Label>
              <Input
                id={`offer-addr-location-${index}`}
                placeholder="Area / landmark"
                value={row.location}
                onChange={(e) =>
                  updateRow(index, { location: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor={`offer-addr-cc-${index}`}>
                  Phone country code
                </Label>
                <Input
                  id={`offer-addr-cc-${index}`}
                  placeholder="+971"
                  value={row.phoneCountryCode}
                  onChange={(e) =>
                    updateRow(index, { phoneCountryCode: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`offer-addr-phone-${index}`}>
                  Phone number
                </Label>
                <Input
                  id={`offer-addr-phone-${index}`}
                  placeholder="Phone number"
                  inputMode="numeric"
                  value={row.phoneNumber}
                  onChange={(e) =>
                    updateRow(index, {
                      phoneNumber: e.target.value.replace(/[^\d]/g, ""),
                    })
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full border-dashed"
        onClick={() => onChange([...rows, createEmptyMerchantOfferAddressRow()])}
      >
        <Plus className="h-4 w-4 mr-2" />
        Add another address
      </Button>
    </div>
  );
}
